import { useState, useEffect } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { UserDetails } from '@/contexts/AuthContext';
import { logError, setSentryUser } from '@/lib/sentry';
import { fetchMarketerPaymentProfile } from '@/lib/payment-profile-rpcs';

export function useAuthState() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let authEventSeen = false;
    let currentUserId: string | null = null;
    let profileGeneration = 0;

    const applySession = (nextSession: Session | null) => {
      const nextUserId = nextSession?.user.id ?? null;
      const identityChanged = nextUserId !== currentUserId;
      if (identityChanged) {
        currentUserId = nextUserId;
        profileGeneration += 1;
        setUserDetails(null);
      }
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      return identityChanged;
    };

    const isCurrentProfile = (generation: number) =>
      mounted && generation === profileGeneration;

    const loadUserDetails = async (userId: string) => {
      const readProfile = () => supabase
        .from('users')
        .select(`
          *,
          marketers (
            paypal_name,
            paypal_verified_at,
            paypal_charges_enabled,
            card_verified_at,
            stripe_charges_enabled,
            primary_payment_method,
            email_notifications,
            status
          )
        `)
        .eq('id', userId)
        .single();
      let { data: userDetailsData, error: fetchError } = await readProfile();
      // A new agency has no creator/marketer subprofile. Recover its own base
      // profile once when SIGNED_IN races the signup/email-confirmation handler.
      if (fetchError?.code === 'PGRST116') {
        const { data: identity } = await supabase.auth.getUser();
        if (identity.user?.id === userId && identity.user.user_metadata?.user_type === 'agency') {
          // Concurrent signup/callback creation is harmless: a duplicate insert
          // loses to the existing primary key, then we read the stored role.
          await supabase.from('users').insert({
            id: userId, user_type: 'agency', email: identity.user.email,
            full_name: identity.user.user_metadata?.full_name || '',
          });
          ({ data: userDetailsData, error: fetchError } = await readProfile());
        }
      }

      if (fetchError || !userDetailsData) {
        return { data: null, error: fetchError };
      }

      const flattenedData = {
        ...userDetailsData,
        ...(userDetailsData.marketers || {})
      } as any;
      delete flattenedData.marketers;

      if (flattenedData.user_type === 'marketer') {
        const { data: paymentProfile, error: paymentProfileError } =
          await fetchMarketerPaymentProfile(userId);

        if (!paymentProfileError && paymentProfile) {
          Object.assign(flattenedData, paymentProfile);
        }
      }

      return { data: flattenedData as UserDetails, error: null };
    };

    const fetchProfile = async (userId: string, generation: number) => {
      try {
        const { data, error } = await loadUserDetails(userId);
        if (!isCurrentProfile(generation)) return;

        if (error) {
          // Missing profiles are expected while signing up.
          if (typeof window === 'undefined' || window.location.pathname !== '/auth') {
            logError('Error fetching user details:', error);
          }
        } else if (data) {
          setUserDetails(data);
        }
      } catch (error) {
        if (isCurrentProfile(generation)) {
          logError('Error fetching user details:', error);
        }
      } finally {
        if (isCurrentProfile(generation)) setLoading(false);
      }
    };

    const initialize = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        // A newer auth event owns state, including an explicit sign-out.
        if (!mounted || authEventSeen) return;

        applySession(initialSession);
        if (initialSession?.user) {
          await fetchProfile(initialSession.user.id, ++profileGeneration);
        } else {
          setLoading(false);
        }
      } catch (error) {
        if (mounted && !authEventSeen) {
          logError('Auth initialization error:', error);
          setLoading(false);
        }
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;
      authEventSeen = true;
      const identityChanged = applySession(nextSession);

      if (!nextSession) {
        setUserDetails(null);
        setLoading(false);
        return;
      }

      if (
        identityChanged ||
        event === 'SIGNED_IN' || event === 'USER_UPDATED' ||
        event === 'PASSWORD_RECOVERY'
      ) {
        // INITIAL_SESSION only loads on an identity change. getSession may have
        // already started (or finished) this same account's bootstrap profile.
        const generation = ++profileGeneration;
        if (identityChanged) setLoading(true);
        // Keep Supabase work outside the auth callback's session lock.
        setTimeout(() => {
          if (isCurrentProfile(generation)) {
            void fetchProfile(nextSession.user.id, generation);
          }
        }, 0);
      }
      // A same-account TOKEN_REFRESHED event keeps a pending profile valid.
    });

    initialize();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Update Sentry user context whenever user details change
  useEffect(() => {
    if (userDetails && userDetails.id === user?.id) {
      setSentryUser({
        id: userDetails.id,
        email: userDetails.email,
        user_type: userDetails.user_type,
        full_name: userDetails.full_name
      });
    } else {
      // Clear the previous identity while a new account profile is loading.
      setSentryUser(null);
    }
  }, [userDetails, user]);

  return {
    user,
    session,
    userDetails,
    loading,
    setUserDetails
  };
}
